import {
    cloneElement,
    useState,
    useMemo,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    View
} from "react-native";
import type IComposerAttachmentRowsProps from "./type";
import type {
    ComposerAttachmentRowsVisibleCountConstantType
} from "./type";
import stylesheet, {
    COMPOSER_ATTACHMENT_MAX_ROWS,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    formatNumber
} from "../../../../helpers/locale";
import Attachment from "../../../attachment";
import Chip from "../../../chip";

const getVisibleCount = ({
    attachments,
    offsets
}: ComposerAttachmentRowsVisibleCountConstantType) => {
    const rows = Array.from(new Set(attachments.map((item) => offsets[item.id]))).sort((a, b) => Number(a) - Number(b));

    const overflowRow = rows[COMPOSER_ATTACHMENT_MAX_ROWS];

    if(overflowRow === undefined) {
        return attachments.length;
    }

    const firstHidden = attachments.findIndex((item) => Number(offsets[item.id]) >= overflowRow);

    return Math.max(1, firstHidden - 1);
};

const ComposerAttachmentRows: FC<IComposerAttachmentRowsProps> = ({
    isInitialAnimated,
    renderAttachment,
    customLocalize,
    attachments,
    customTheme,
    style,
    title
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        measure: measureDynamicStyle
    } = useStyles({
        spaces
    });

    const [
        visibleCount,
        setVisibleCount
    ] = useState<number | null>(null);

    const [
        width,
        setWidth
    ] = useState<number>(0);

    const measureKey = `${width}|${attachments.map((item) => item.id).join("|")}`;

    const offsets = useMemo<Record<string, number>>(() => ({
    }), [
        measureKey
    ]);

    const handleLayout = (event: LayoutChangeEvent) => {
        setWidth(Math.round(event.nativeEvent.layout.width));
    };

    const handleItemLayout = (id: string, event: LayoutChangeEvent) => {
        offsets[id] = Math.round(event.nativeEvent.layout.y);

        if(Object.keys(offsets).length !== attachments.length) {
            return;
        }

        setVisibleCount(getVisibleCount({
            attachments,
            offsets
        }));
    };

    const shownCount = Math.min(visibleCount ?? attachments.length, attachments.length);

    const hiddenCount = attachments.length - shownCount;

    return <View
        style={stylesheet.container}
        onLayout={handleLayout}
    >
        <View
            importantForAccessibility="no-hide-descendants"
            style={[
                stylesheet.measure,
                measureDynamicStyle
            ]}
            aria-hidden={true}
            key={measureKey}
        >
            {
                attachments.map((item) => cloneElement(renderAttachment(item), {
                    onLayout: (event: LayoutChangeEvent) => handleItemLayout(item.id, event)
                }))
            }
        </View>
        <Attachment.Group
            isInitialAnimated={isInitialAnimated}
            customLocalize={customLocalize}
            customTheme={customTheme}
            layoutBehaviour="wrap"
            isInField={true}
            style={style}
            title={title}
        >
            {attachments.slice(0, shownCount).map(renderAttachment)}
            {
                hiddenCount > 0 ?
                    <Chip
                        customLocalize={customLocalize}
                        title={`+${formatNumber({
                            locale: activeLocale,
                            value: hiddenCount
                        })}`}
                        customTheme={customTheme}
                        variant="outline"
                        type="neutral"
                        key="overflow"
                        size="small"
                    />
                :
                    null
            }
        </Attachment.Group>
    </View>;
};
export default ComposerAttachmentRows;
